import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';

const TILE_SIZE = 256;
const MIN_LAT = -85.0511;
const MAX_LAT = 85.0511;
const MIN_LON = -180;
const MAX_LON = 180;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const clampZoom = (value) => clamp(value, 2, 18);

const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const latLonToWorld = (lat, lon, zoom) => {
  const safeLat = clamp(lat, MIN_LAT, MAX_LAT);
  const safeLon = clamp(lon, MIN_LON, MAX_LON);
  const scale = TILE_SIZE * 2 ** zoom;
  const x = ((safeLon + 180) / 360) * scale;
  const sinLat = Math.sin((safeLat * Math.PI) / 180);
  const y =
    (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
};

const worldToLatLon = (x, y, zoom) => {
  const scale = TILE_SIZE * 2 ** zoom;
  const lon = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return {
    lat: clamp(lat, MIN_LAT, MAX_LAT),
    lon: clamp(lon, MIN_LON, MAX_LON),
  };
};

const getZoomForBounds = (points, width, height) => {
  if (!points.length) {
    return 3;
  }
  if (points.length === 1) {
    return 14;
  }

  for (let zoom = 16; zoom >= 2; zoom -= 1) {
    const world = points.map((point) => latLonToWorld(point.lat, point.lon, zoom));
    const xs = world.map((point) => point.x);
    const ys = world.map((point) => point.y);
    const spanX = Math.max(...xs) - Math.min(...xs);
    const spanY = Math.max(...ys) - Math.min(...ys);
    if (spanX <= width * 0.72 && spanY <= height * 0.68) {
      return zoom;
    }
  }

  return 2;
};

const statusColor = (status) => {
  if (status === 'UP') return '#16A34A';
  if (status === 'DEGRADED') return '#D97706';
  if (status === 'DOWN') return '#DC2626';
  return '#64748B';
};

const groupStatusColor = (gateways) => {
  if (gateways.some((item) => item.status === 'DOWN')) return '#DC2626';
  if (gateways.some((item) => item.status === 'DEGRADED')) return '#D97706';
  if (gateways.some((item) => item.status === 'UP')) return '#16A34A';
  return '#64748B';
};

const statusLabel = (status) => {
  if (status === 'UP') return 'Доступен';
  if (status === 'DEGRADED') return 'Нестабильно';
  if (status === 'DOWN') return 'Недоступен';
  return 'Неизвестно';
};

const gatewayTypeLabel = (type) => {
  if (type === 'USPD') return 'УСПД';
  if (type === 'BS') return 'Базовая станция';
  return type || 'Шлюз';
};

const pathIssueChip = (gateway) => {
  const focus = gateway?.path_issue_focus;
  const label = gateway?.path_issue_label;
  if (!focus || focus === 'unknown') return null;
  const color =
    focus === 'gateway'
      ? '#DC2626'
      : focus === 'ok'
        ? '#16A34A'
        : '#D97706';
  const devicesTotal = Number(gateway?.devices_total || 0);
  const devicesProblem = Number(gateway?.devices_problem || 0);
  const count =
    devicesTotal > 0 ? ` · ${devicesProblem}/${devicesTotal}` : '';
  return { label: `${label || 'Диагноз'}${count}`, color };
};

const zoomViewportAroundPoint = (viewport, nextZoom, anchorX, anchorY, size) => {
  const safeZoom = clampZoom(nextZoom);
  const prevScale = 2 ** viewport.zoom;
  const nextScale = 2 ** safeZoom;
  const worldX = viewport.centerX + (anchorX - size.width / 2);
  const worldY = viewport.centerY + (anchorY - size.height / 2);
  const scaledWorldX = (worldX / prevScale) * nextScale;
  const scaledWorldY = (worldY / prevScale) * nextScale;

  return {
    zoom: safeZoom,
    centerX: scaledWorldX - (anchorX - size.width / 2),
    centerY: scaledWorldY - (anchorY - size.height / 2),
  };
};

export default function GatewayMapView({
  gateways = [],
  selectedGatewayId = null,
  onSelectGateway,
  onPickCoordinates,
  interactive = false,
  height = 420,
}) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [size, setSize] = useState({ width: 900, height });
  const [viewport, setViewport] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect;
      if (!next) return;
      setSize({
        width: Math.max(320, Math.round(next.width)),
        height: Math.max(240, Math.round(next.height)),
      });
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const points = useMemo(
    () =>
      gateways
        .map((gateway) => {
          const lat = normalizeCoordinate(gateway.latitude);
          const lon = normalizeCoordinate(gateway.longitude);
          if (lat === null || lon === null) {
            return null;
          }
          return {
            id: gateway.id,
            gateway,
            lat,
            lon,
          };
        })
        .filter(Boolean),
    [gateways]
  );

  const baseViewport = useMemo(() => {
    const zoom = getZoomForBounds(points, size.width, size.height);
    if (!points.length) {
      const center = latLonToWorld(55.751244, 37.618423, zoom);
      return { zoom, centerX: center.x, centerY: center.y };
    }

    const world = points.map((point) => latLonToWorld(point.lat, point.lon, zoom));
    const centerX =
      world.reduce((sum, point) => sum + point.x, 0) / Math.max(1, world.length);
    const centerY =
      world.reduce((sum, point) => sum + point.y, 0) / Math.max(1, world.length);

    return { zoom, centerX, centerY };
  }, [points, size.height, size.width]);

  useEffect(() => {
    setViewport(baseViewport);
  }, [baseViewport]);

  const activeViewport = viewport || baseViewport;

  const tiles = useMemo(() => {
    const { zoom, centerX, centerY } = activeViewport;
    const left = centerX - size.width / 2;
    const top = centerY - size.height / 2;
    const right = centerX + size.width / 2;
    const bottom = centerY + size.height / 2;
    const tileCount = 2 ** zoom;
    const tileMinX = Math.floor(left / TILE_SIZE);
    const tileMaxX = Math.floor(right / TILE_SIZE);
    const tileMinY = Math.floor(top / TILE_SIZE);
    const tileMaxY = Math.floor(bottom / TILE_SIZE);
    const result = [];

    for (let tileX = tileMinX; tileX <= tileMaxX; tileX += 1) {
      for (let tileY = tileMinY; tileY <= tileMaxY; tileY += 1) {
        if (tileY < 0 || tileY >= tileCount) {
          continue;
        }
        const wrappedX = ((tileX % tileCount) + tileCount) % tileCount;
        result.push({
          key: `${zoom}-${wrappedX}-${tileY}`,
          src: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${tileY}.png`,
          left: tileX * TILE_SIZE - left,
          top: tileY * TILE_SIZE - top,
        });
      }
    }

    return result;
  }, [activeViewport, size.height, size.width]);

  const markerPositions = useMemo(() => {
    const { zoom, centerX, centerY } = activeViewport;
    const left = centerX - size.width / 2;
    const top = centerY - size.height / 2;
    const grouped = new Map();

    points.forEach((point) => {
      const key = `${point.lat.toFixed(6)}:${point.lon.toFixed(6)}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          lat: point.lat,
          lon: point.lon,
          gateways: [],
        });
      }
      grouped.get(key).gateways.push(point.gateway);
    });

    return Array.from(grouped.values()).map((group) => {
      const world = latLonToWorld(group.lat, group.lon, zoom);
      return {
        ...group,
        left: world.x - left,
        top: world.y - top,
      };
    });
  }, [activeViewport, points, size.height, size.width]);

  const handleZoomStep = (delta) => {
    setViewport((current) =>
      zoomViewportAroundPoint(
        current || baseViewport,
        (current || baseViewport).zoom + delta,
        size.width / 2,
        size.height / 2,
        size
      )
    );
  };

  const handleResetViewport = () => {
    setViewport(baseViewport);
  };

  const stopMapEvent = (event) => {
    event.stopPropagation();
  };

  const stopMapPointerEvent = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handlePointerDown = (event) => {
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: viewport || baseViewport,
      moved: false,
    };
    setIsDragging(false);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    const moved = Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3;
    dragRef.current.moved = dragRef.current.moved || moved;

    if (dragRef.current.moved) {
      setIsDragging(true);
    }

    setViewport({
      ...dragRef.current.origin,
      centerX: dragRef.current.origin.centerX - deltaX,
      centerY: dragRef.current.origin.centerY - deltaY,
    });
  };

  const handlePointerFinish = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    window.requestAnimationFrame(() => setIsDragging(false));
    dragRef.current = null;
  };

  const handlePick = (event) => {
    if (dragRef.current?.moved || isDragging) {
      return;
    }

    if (openGroupId) {
      setOpenGroupId(null);
      return;
    }

    if (!interactive || !onPickCoordinates || !containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    const worldX = activeViewport.centerX - size.width / 2 + clickX;
    const worldY = activeViewport.centerY - size.height / 2 + clickY;
    const result = worldToLatLon(worldX, worldY, activeViewport.zoom);

    onPickCoordinates({
      latitude: Number(result.lat.toFixed(6)),
      longitude: Number(result.lon.toFixed(6)),
    });
  };

  const handleMarkerClick = (event, group) => {
    event.stopPropagation();
    if (group.gateways.length <= 1) {
      onSelectGateway?.(group.gateways[0]);
      setOpenGroupId(null);
      return;
    }
    setOpenGroupId((current) => (current === group.id ? null : group.id));
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        borderColor: 'rgba(15,23,42,0.08)',
        background: '#F8FAFC',
      }}
    >
      <Box
        ref={containerRef}
        onClick={handlePick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerFinish}
        onPointerCancel={handlePointerFinish}
        onPointerLeave={handlePointerFinish}
        sx={{
          position: 'relative',
          width: '100%',
          height,
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : interactive ? 'crosshair' : 'grab',
          background:
            'linear-gradient(180deg, rgba(226,232,240,0.7), rgba(248,250,252,0.9))',
          touchAction: 'none',
        }}
      >
        <Stack
          onClick={stopMapEvent}
          onPointerDown={stopMapPointerEvent}
          onPointerUp={stopMapEvent}
          onWheel={stopMapEvent}
          spacing={0.75}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 5,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => handleZoomStep(1)}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              p: 0,
              borderRadius: 2.25,
              boxShadow: '0 10px 24px rgba(15,23,42,0.14)',
            }}
          >
            <AddRoundedIcon fontSize="small" />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleZoomStep(-1)}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              p: 0,
              borderRadius: 2.25,
              boxShadow: '0 10px 24px rgba(15,23,42,0.14)',
            }}
          >
            <RemoveRoundedIcon fontSize="small" />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetViewport}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              p: 0,
              borderRadius: 2.25,
              background: 'rgba(255,255,255,0.92)',
              borderColor: 'rgba(148,163,184,0.34)',
            }}
          >
            <CenterFocusStrongRoundedIcon fontSize="small" />
          </Button>
        </Stack>

        {tiles.map((tile) => (
          <Box
            key={tile.key}
            component="img"
            src={tile.src}
            alt=""
            draggable={false}
            sx={{
              position: 'absolute',
              left: tile.left,
              top: tile.top,
              width: TILE_SIZE,
              height: TILE_SIZE,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        ))}

        {markerPositions.map((group) => {
          const isSelected = group.gateways.some(
            (gateway) => String(selectedGatewayId || '') === String(gateway.id)
          );
          const isMulti = group.gateways.length > 1;
          const markerColor = isMulti
            ? groupStatusColor(group.gateways)
            : statusColor(group.gateways[0]?.status);
          const popupWidth = 280;
          const popupMaxHeight = Math.min(320, Math.max(180, size.height - 48));
          const popupEstimatedHeight = Math.min(
            popupMaxHeight,
            52 + group.gateways.length * 74
          );
          const popupMargin = 12;
          const popupLeft = clamp(
            group.left + 18,
            popupMargin,
            Math.max(popupMargin, size.width - popupWidth - popupMargin)
          );
          const preferredTop = group.top - popupEstimatedHeight - 18;
          const fallbackTop = group.top + 18;
          const popupTop = clamp(
            preferredTop >= popupMargin ? preferredTop : fallbackTop,
            popupMargin,
            Math.max(popupMargin, size.height - popupEstimatedHeight - popupMargin)
          );

          return (
            <Box
              key={group.id}
              sx={{
                position: 'absolute',
                left: group.left,
                top: group.top,
                zIndex: isSelected || openGroupId === group.id ? 4 : 2,
              }}
            >
              <Tooltip
                title={
                  isMulti
                    ? `В этой точке ${group.gateways.length} УСПД`
                    : (() => {
                        const gw = group.gateways[0];
                        const diagnosis = gw?.path_issue_label
                          ? ` · ${gw.path_issue_label}`
                          : '';
                        return `${gw?.name || 'Без названия'} · ${statusLabel(gw?.status)}${diagnosis}`;
                      })()
                }
              >
                <Box
                  data-map-marker="true"
                  onPointerDown={stopMapPointerEvent}
                  onClick={(event) => handleMarkerClick(event, group)}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    transform: 'translate(-50%, -100%)',
                    cursor: onSelectGateway ? 'pointer' : 'default',
                  }}
                >
                  <Box
                    sx={{
                      width: isSelected ? 22 : isMulti ? 24 : 18,
                      height: isSelected ? 22 : isMulti ? 24 : 18,
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      background: markerColor,
                      border: '2px solid rgba(255,255,255,0.92)',
                      boxShadow: '0 6px 16px rgba(15,23,42,0.18)',
                    }}
                  />
                  {isMulti && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -10,
                        minWidth: 20,
                        height: 20,
                        px: 0.5,
                        borderRadius: 999,
                        background: '#0F172A',
                        color: '#FFFFFF',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        boxShadow: '0 6px 14px rgba(15,23,42,0.18)',
                      }}
                    >
                      {group.gateways.length}
                    </Box>
                  )}
                </Box>
              </Tooltip>

              {openGroupId === group.id && isMulti && (
                <Paper
                  variant="outlined"
                  onPointerDown={stopMapPointerEvent}
                  onClick={stopMapEvent}
                  sx={{
                    position: 'absolute',
                    top: popupTop - group.top,
                    left: popupLeft - group.left,
                    width: popupWidth,
                    maxWidth: popupWidth,
                    borderRadius: 2.5,
                    borderColor: 'rgba(15,23,42,0.08)',
                    background: 'rgba(255,255,255,0.96)',
                    boxShadow: '0 18px 40px rgba(15,23,42,0.16)',
                    backdropFilter: 'blur(10px)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.25,
                      py: 1,
                      borderBottom: '1px solid rgba(15,23,42,0.06)',
                      background: 'rgba(248,250,252,0.92)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      В этой точке: {group.gateways.length}
                    </Typography>
                  </Box>
                  <Stack
                    spacing={0.75}
                    sx={{
                      p: 1,
                      maxHeight: popupMaxHeight,
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    {group.gateways.map((gateway) => {
                      const gatewaySelected =
                        String(selectedGatewayId || '') === String(gateway.id);
                      const secondaryText =
                        gateway.path_issue_reason ||
                        gateway.address ||
                        gateway.location ||
                        gateway.host ||
                        'Адрес не указан';
                      const diagnosis = pathIssueChip(gateway);
                      return (
                        <Button
                          key={gateway.id}
                          variant={gatewaySelected ? 'contained' : 'text'}
                          size="small"
                          onClick={() => {
                            onSelectGateway?.(gateway);
                            setOpenGroupId(null);
                          }}
                          sx={{
                            justifyContent: 'flex-start',
                            borderRadius: 2,
                            px: 1,
                            py: 0.85,
                            minHeight: 54,
                            textTransform: 'none',
                            boxShadow: 'none',
                            flexShrink: 0,
                          }}
                        >
                          <Stack spacing={0.45} sx={{ width: '100%', minWidth: 0 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ width: '100%', minWidth: 0 }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: statusColor(gateway.status),
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontWeight: 600,
                                }}
                              >
                                {gateway.name || 'Без названия'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label={gatewayTypeLabel(gateway.gateway_type)}
                                sx={{
                                  height: 22,
                                  borderRadius: 999,
                                  background: 'rgba(37,99,235,0.08)',
                                  color: '#1D4ED8',
                                  fontSize: 11,
                                }}
                              />
                              <Chip
                                size="small"
                                label={statusLabel(gateway.status)}
                                sx={{
                                  height: 22,
                                  borderRadius: 999,
                                  background: `${statusColor(gateway.status)}18`,
                                  color: statusColor(gateway.status),
                                  fontSize: 11,
                                }}
                              />
                              {diagnosis ? (
                                <Chip
                                  size="small"
                                  label={diagnosis.label}
                                  sx={{
                                    height: 22,
                                    borderRadius: 999,
                                    background: `${diagnosis.color}18`,
                                    color: diagnosis.color,
                                    fontSize: 11,
                                  }}
                                />
                              ) : null}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'left',
                              }}
                            >
                              {secondaryText}
                            </Typography>
                          </Stack>
                        </Button>
                      );
                    })}
                  </Stack>
                </Paper>
              )}
            </Box>
          );
        })}

        {!points.length && (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(248,250,252,0.72)',
            }}
          >
            <PlaceRoundedIcon sx={{ color: 'text.secondary', fontSize: 32 }} />
            <Typography variant="body2" color="text.secondary">
              Нет шлюзов с координатами
            </Typography>
            {interactive && (
              <Chip size="small" label="Нажмите на карту, чтобы выбрать точку" />
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
